import { createForm } from "@felte/solid";
import { validator } from "@felte/validator-zod";
import {
  LinkExternal01,
  Trash01,
} from "@liftedcare/solidjs-untitled-icons/duocolor";
import { HelpCircle } from "@liftedcare/solidjs-untitled-icons/line";
import { useMutation, useQuery } from "@tanstack/solid-query";
import { format } from "date-fns";
import { Component, createEffect, createSignal, Index, Show } from "solid-js";

import gqlClient from "../../schema/gqlAuthorizedRequest.js";
import { graphql } from "../../schema/index.js";
import { MutationError } from "../../utils/errors.js";
import { getGqlErrorMessage } from "../../utils/getGqlErrorMessage.js";
import { Button } from "../common/Button.js";
import CardContainer from "../common/CardContainer.js";
import FormRow from "../common/FormRow.js";
import AddressInput from "../form/AddressInput.js";
import { Checkbox } from "../form/Checkbox.js";
import DateInput from "../form/DateInput.js";
import { FormContextProvider } from "../form/FormContext.js";
import { FormLabel } from "../form/FormLabel.js";
import Input from "../form/Input.js";
import Select from "../form/Select.js";
import { TextArea } from "../form/TextArea.js";
import Modal, { ModalBody, ModalFooter, ModalHeader } from "../modal/Modal.js";
import { useToast } from "../toast/index.js";
import { smsFormValidationSchema } from "./SmsFormValidationSchema.js";
import {
  smsCharityOptions,
  smsCountryOptions,
  smsOrganisationTradingPeriod,
  smsTitleOptions,
  smsTypeOfBody,
  smsTypeOfOrganisation,
  smsUkRegion,
} from "./SmsFormValues.js";

const COS_NUMBER_REASON = `We provide care to people who require support with the activities of daily living. We have struggled to recruit a sufficient volume of staff with the availability and skills required in our local area. We have tried online and offline activity to find these staff. As a result we would like to sponsor staff. We have identified the following individuals in the UK who require sponsorship on the health and social care worker visa.`;

interface SmsContact {
  title: string | null;
  otherTitle: string | null;
  firstName: string;
  lastName: string;
  previousName: string | null;
  employmentAddress: {
    line1: string;
    line2: string | null;
    line3: string | null;
    townOrCity: string;
    county: string | null;
    postcode: string;
    phoneNumber: string;
    email: string;
    dateOfBirth: Date | null;
    nationalityCountry: string | null; // Use values found on sms page dropdown
    positionWithinOrganisation: string;
  };
  // hasNationalInsuranceNumber: boolean;
  nationalInsuranceNumber: string | null;
  // nationalInsuranceNumberExemption: string | null;
  // isNonSettledUnderImmigrationControl: boolean;
  // immigrationStatus: string | null;
  // homeOfficeReference: string | null;
  // dateCurrentLeaveToEnterOrLeaveEnds: Date | null;
  // passportNumber: string;
  // hasOfficerBeenConvictedOrBankrupt: boolean;
  // convictionOrBankruptDetails: string | null;
}

interface SmsFormValues {
  organisationLegalName: string;
  organisationLegalAddress: {
    line1: string;
    line2: string | null;
    postcode: string;
    townOrCity: string;
    county: string;
    country: string;
    phoneNumber: string;
  };
  orgSameTradingName: boolean;
  organisationTradingName: string;
  organisationTradingAddress: {
    line1: string;
    line2: string | null;
    postcode: string;
    townOrCity: string;
    county: string;
    country: string;
    phoneNumber: string;
  };
  anyPreviousTradingNames: boolean;
  previousTradingNames:
    | {
        tradingName: string;
        address: {
          line_1: string;
          line_2: string | null;
          line_3: string | null;
          line_4: string | null;
          townOrCity: string;
          county: string;
          country: string;
          phoneNumber: string;
        };
        website: string;
        tradingFrom: Date | null;
        tradingTo: Date | null;
      }[]
    | null;
  operatingRegion: string | null;
  currentEmployeesNumber: number | null;
  currentlyEmployingMigrantWorkers: boolean;
  currentlyEmployedMigrantWorkersNum: number | null;
  typeOfBody: string | null;
  typeOfBodyDetails: string | null;
  holdingCharitableStatus: boolean;
  charityRegStatus: string | null;
  charityRegNumber: string | null;
  typeOfOrganisation: string | null;
  companiesHouseRegistrationNumber: string | null;
  organisationTradingPeriod: string | null;
  isVatRegistered: boolean;
  vatRegistrationNumber: string | null;
  accountsOfficeReferenceNumber: string;
  payeReferenceNumbers: { payeReference: string }[];
  // registeredOnStockExchange: boolean;
  // stockExchangeName: string | null;
  // registeredToAccreditedBodies:
  //   | {
  //       bodyName: string;
  //       bodyNumber: string | null;
  //       expiryDate: Date | null;
  //     }[]
  //   | null;
  cqcNumber: string;
  numberOfCos: number | null;
  reasonForNumberOfCos: string | null;
  organisationAuthorisingOfficer: // | (SmsContact & {
  //     isKeyContact: boolean;
  //   })
  SmsContact | null;
  // keyContact: SmsContact | null;
  // levelOneUsers: SmsContact[];
  agreeWithAllTheStatementsDate: string | null;
  // documents: {
  //   "sponsorAppDocs:priDoc:2:provideSingle": boolean;
  //   "sponsorAppDocs:priDoc:3:provideSingle": boolean;
  //   "sponsorAppDocs:secDoc:0:provideSingle": boolean;
  //   "sponsorAppDocs:secDoc:1:provideSingle": boolean;
  //   "sponsorAppDocs:secDoc:2:provideSingle": boolean;
  //   "sponsorAppDocs:secDoc:3:provideSingle": boolean;
  //   "sponsorAppDocs:secDoc:4:provideSingle": boolean;
  //   "sponsorAppDocs:secDoc:5:provideSingle": boolean;
  //   "sponsorAppDocs:secDoc:6:provideSingle": boolean;
  //   "sponsorAppDocs:secDoc:7:provideSingle": boolean;
  // };
  usingAgencyStaff: boolean;
  officeStaffCarryingOutCareCalls: boolean;
  numberOfPartTimeCarers: number | null;
  numberOfFullTimeCarers: number | null;
  numberOfCarersResigned: number | null;
  triedToRecruitFromWithinTheUK: string | null;
  whyApplyingForSponsorLicence: string | null;
  hasReadGuidance: boolean;
}

const emptySmsContact: SmsContact = {
  title: null,
  otherTitle: null,
  firstName: "",
  lastName: "",
  previousName: null,
  employmentAddress: {
    line1: "",
    line2: null,
    line3: null,
    townOrCity: "",
    county: null,
    postcode: "",
    phoneNumber: "",
    email: "",
    dateOfBirth: null,
    nationalityCountry: null,
    positionWithinOrganisation: "",
  },
  // hasNationalInsuranceNumber: false,
  nationalInsuranceNumber: null,
  // nationalInsuranceNumberExemption: null,
  // isNonSettledUnderImmigrationControl: false,
  // immigrationStatus: null,
  // homeOfficeReference: null,
  // dateCurrentLeaveToEnterOrLeaveEnds: null,
  // passportNumber: "",
  // hasOfficerBeenConvictedOrBankrupt: false,
  // convictionOrBankruptDetails: null,
};

const initialData = {
  organisationLegalName: "",
  organisationLegalAddress: {
    line1: "",
    line2: null,
    postcode: "",
    townOrCity: "",
    county: "",
    country: "",
    phoneNumber: "",
  },
  orgSameTradingName: false,
  organisationTradingName: "",
  organisationTradingAddress: {
    line1: "",
    line2: null,
    postcode: "",
    townOrCity: "",
    county: "",
    country: "",
    phoneNumber: "",
  },
  anyPreviousTradingNames: false,
  previousTradingNames: [],
  operatingRegion: null,
  currentEmployeesNumber: null,
  currentlyEmployingMigrantWorkers: false,
  currentlyEmployedMigrantWorkersNum: null,
  typeOfBody: null,
  typeOfBodyDetails: null,
  holdingCharitableStatus: false,
  charityRegStatus: null,
  charityRegNumber: null,
  typeOfOrganisation: null,
  companiesHouseRegistrationNumber: "",
  organisationTradingPeriod: null,
  isVatRegistered: false,
  vatRegistrationNumber: "",
  accountsOfficeReferenceNumber: "",
  payeReferenceNumbers: [],
  // registeredOnStockExchange: false,
  // stockExchangeName: null,
  // registeredToAccreditedBodies: [],
  cqcNumber: "",
  numberOfCos: null,
  reasonForNumberOfCos: COS_NUMBER_REASON,
  // organisationAuthorisingOfficer: { isKeyContact: false, ...emptySmsContact },
  organisationAuthorisingOfficer: { ...emptySmsContact },
  // keyContact: {
  //   ...emptySmsContact,
  // },
  // levelOneUsers: [],
  agreeWithAllTheStatementsDate: format(new Date(), "yyyy-MM-dd"),
  // documents: {
  //   "sponsorAppDocs:priDoc:2:provideSingle": false,
  //   "sponsorAppDocs:priDoc:3:provideSingle": false,
  //   "sponsorAppDocs:secDoc:0:provideSingle": false,
  //   "sponsorAppDocs:secDoc:1:provideSingle": false,
  //   "sponsorAppDocs:secDoc:2:provideSingle": false,
  //   "sponsorAppDocs:secDoc:3:provideSingle": false,
  //   "sponsorAppDocs:secDoc:4:provideSingle": false,
  //   "sponsorAppDocs:secDoc:5:provideSingle": false,
  //   "sponsorAppDocs:secDoc:6:provideSingle": false,
  //   "sponsorAppDocs:secDoc:7:provideSingle": false,
  // },
  usingAgencyStaff: false,
  officeStaffCarryingOutCareCalls: false,
  numberOfPartTimeCarers: null,
  numberOfFullTimeCarers: null,
  numberOfCarersResigned: null,
  triedToRecruitFromWithinTheUK: null,
  whyApplyingForSponsorLicence: null,
  hasReadGuidance: false,
} satisfies SmsFormValues;

const getSmsQuestionnaireQuery = graphql(`
  query getSmsQuestionnaire($agencyId: Int) {
    getSmsQuestionnaire(agencyId: $agencyId)
  }
`);

const updateSmsQuestionnaireMutation = graphql(`
  mutation updateSmsQuestionnaire($agencyId: Int, $questionnaire: String!) {
    updateSmsQuestionnaire(
      agencyId: $agencyId
      smsQuestionnaire: $questionnaire
    ) {
      success
    }
  }
`);

const countryOptions = smsCountryOptions;

const SmsForm: Component<{ agencyId?: number }> = (props) => {
  const toast = useToast();

  const [showFinalAssumptionsModal, setShowFinalAssumptionsModal] =
    createSignal(false);
  const [showCareQualityGuidance, setShowCareQualityGuidance] =
    createSignal(false);
  const [showAccountReferenceNumberInfo, setShowAccountReferenceNumberInfo] =
    createSignal(false);

  const getSmsQuestionnaire = useQuery(() => ({
    queryKey: ["smsQuestionnaire", props.agencyId],
    queryFn: async () => {
      const data = await gqlClient.request(getSmsQuestionnaireQuery, {
        agencyId: props.agencyId ?? undefined,
      });
      return data;
    },
  }));

  const updateSmsQuestionnaire = useMutation(() => ({
    mutationFn: async (variables: { questionnaire: string }) => {
      return gqlClient.request(updateSmsQuestionnaireMutation, {
        agencyId: props.agencyId,
        ...variables,
      });
    },
    onSuccess: () => {
      toast.success("Success", "Sms questionnaire saved");
    },
    onError(error: MutationError) {
      toast.error(
        "Error",
        getGqlErrorMessage(error, "Error saving sms questionnaire"),
      );
    },
  }));

  const formMethods = createForm({
    onSubmit: (values) => {
      setShowFinalAssumptionsModal(true);
      updateSmsQuestionnaire.mutate({ questionnaire: JSON.stringify(values) });
    },
    extend: validator({ schema: smsFormValidationSchema }),
    initialValues: initialData,
  });

  createEffect(() => {
    const value = getSmsQuestionnaire.data?.getSmsQuestionnaire;
    formMethods.reset();
    if (value) {
      formMethods.setData(JSON.parse(value));
    }
  });
  const form = formMethods.form;

  // createRenderEffect(() => {
  //   if (formMethods.data("organisationAuthorisingOfficer.isKeyContact")) {
  //     formMethods.setData("keyContact", null as any);
  //   } else {
  //     formMethods.setData("keyContact", emptySmsContact);
  //   }
  // });

  const addPreviousTradingName = () => {
    formMethods.addField("previousTradingNames", {
      tradingName: "",
      address: {
        line1: "",
        line2: "",
        townOrCity: "",
        county: "",
        country: "",
        phoneNumber: "",
      },
      website: "",
      tradingFrom: null,
      tradingTo: null,
    });
  };

  const removeTradingName = (index: number) => {
    formMethods.unsetField(`previousTradingNames.${index}`);
  };

  const addPayeReferenceNumber = () => {
    formMethods.addField("payeReferenceNumbers", { payeReference: "" });
  };

  const removePayeReference = (index: number) => {
    formMethods.unsetField(`payeReferenceNumbers.${index}`);
  };

  // const addRegisteredToAccreditedBodiesValue = () => {
  //   formMethods.addField("registeredToAccreditedBodies", { payeReference: "" });
  // };

  // const removeRegisteredToAccreditedBodiesValue = (index: number) => {
  //   formMethods.unsetField(`registeredToAccreditedBodies.${index}`);
  // };

  // const addLevelOneUser = () => {
  //   formMethods.addField("levelOneUsers", emptySmsContact);
  // };

  // const removeLevelOneUser = (index: number) => {
  //   formMethods.unsetField(`levelOneUsers.${index}`);
  // };

  return (
    <Show when={props.agencyId} fallback={"Agency missing"}>
      <FormContextProvider formMethods={formMethods}>
        <form use:form class="space-y-4">
          <CardContainer class="flex flex-col !space-y-0 !p-0">
            <FormRow
              label="Organisation legal name"
              subLabel="(as registered with Companies' House)"
            >
              <Input name="organisationLegalName" />
            </FormRow>
            <FormRow label="Organisation address" labelClass="self-start mt-6">
              <div class="rounded-lg border">
                <AddressInput
                  name="organisationLegalAddress"
                  countryOptions={smsCountryOptions}
                  editable
                  containerClass="last:border-b-0"
                />
              </div>
            </FormRow>
            <FormRow
              label="Organisation phone number"
              labelClass="!whitespace-normal"
            >
              <Input name="organisationLegalAddress.phoneNumber" />
            </FormRow>

            <FormRow
              label="Tick to confirm that your organisation is operating under the same name"
              labelClass="!whitespace-normal"
            >
              <Checkbox name="orgSameTradingName" />
            </FormRow>
            <Show when={!formMethods.data("orgSameTradingName")}>
              <FormRow
                label="Organisation trading name"
                subLabel="(the name under which the business operates)"
              >
                <Input name="organisationTradingName" />
              </FormRow>
              <FormRow
                label="Organisation trading address"
                labelClass="self-start mt-6"
              >
                <div class="rounded-lg border">
                  <AddressInput
                    name="organisationTradingAddress"
                    countryOptions={smsCountryOptions}
                    editable
                    containerClass="last:border-b-0"
                  />
                </div>
              </FormRow>
              <FormRow
                label="Organisation trading phone number"
                labelClass="!whitespace-normal"
              >
                <Input name="organisationTradingAddress.phoneNumber" />
              </FormRow>
            </Show>
            <FormRow
              label="Tick if the organisation has traded under any other name in past 4 years"
              labelClass="!whitespace-normal"
            >
              <Checkbox name="anyPreviousTradingNames" />
            </FormRow>
            <Show when={formMethods.data("anyPreviousTradingNames")}>
              <div class="border-b py-6">
                <div class="flex flex-row items-center justify-between px-8">
                  <FormLabel>Previous trading names</FormLabel>
                  <Button onClick={addPreviousTradingName}>
                    Add previous trading name
                  </Button>
                </div>
                <Index each={formMethods.data("previousTradingNames") || []}>
                  {(_, index) => {
                    return (
                      <FormRow
                        label={
                          <div class="flex items-center gap-4">
                            Trading name {index + 1}
                            <button
                              onClick={() => removeTradingName(index)}
                              class="text-red-500"
                            >
                              <Trash01 class="w-5" />
                            </button>
                          </div>
                        }
                        labelClass="self-start mt-6"
                      >
                        <SmsFormPreviousTradingName
                          name={`previousTradingNames.${index}`}
                        />
                      </FormRow>
                    );
                  }}
                </Index>
              </div>
            </Show>
            <FormRow label="Where do you deliver care?">
              <Select name="operatingRegion" options={smsUkRegion} />
              {/* dropdown wales, scotland,england,northern ireland */}
            </FormRow>
            <FormRow label="How many employees do you have?">
              <Input type="number" name="currentEmployeesNumber" />
            </FormRow>
            <FormRow label="Tick if you are already sponsoring workers">
              <Checkbox name="currentlyEmployingMigrantWorkers" />
            </FormRow>
            <Show when={formMethods.data("currentlyEmployingMigrantWorkers")}>
              <FormRow
                label="How many workers are you currently sponsoring?"
                labelClass="!whitespace-normal"
              >
                <Input
                  type="number"
                  name="currentlyEmployedMigrantWorkersNum"
                />
              </FormRow>
            </Show>
            <FormRow label="What kind of company are you?">
              <Select name="typeOfBody" options={smsTypeOfBody} />{" "}
              {/* select between HEAD OFFICE and single body */}
            </FormRow>
            <Show
              when={
                formMethods.data("typeOfBody") &&
                ["HEADUKBR", "GRPBR", "OTHER"].includes(
                  formMethods.data("typeOfBody")!,
                )
              }
            >
              <FormRow
                label="Type of body details"
                labelClass="!whitespace-normal"
              >
                <TextArea name="typeOfBodyDetails" class="!p-2" />
              </FormRow>
            </Show>
            <FormRow
              label="Tick if your organisation is a registered charity"
              labelClass="!whitespace-normal"
            >
              <Checkbox name="holdingCharitableStatus" />
            </FormRow>
            <Show when={formMethods.data("holdingCharitableStatus")}>
              <FormRow label="Charity registration status">
                <Select name="charityRegStatus" options={smsCharityOptions} />
              </FormRow>
              <FormRow
                label="Charity registration number or details why it does not need to be registered"
                labelClass="!whitespace-normal"
              >
                <Input name="charityRegNumber" />
              </FormRow>
            </Show>
            <FormRow label="What is your organisation type?">
              <Select
                name="typeOfOrganisation"
                options={smsTypeOfOrganisation}
              />
            </FormRow>
            <FormRow label="Companies House registration number">
              <Input name="companiesHouseRegistrationNumber" />
            </FormRow>
            <FormRow
              label="How long have you been trading?"
              labelClass="!whitespace-normal"
            >
              <Select
                name="organisationTradingPeriod"
                options={smsOrganisationTradingPeriod}
              />
            </FormRow>
            <FormRow label="Tick if your organisation is VAT registered">
              <Checkbox name="isVatRegistered" />
            </FormRow>
            <Show when={formMethods.data("isVatRegistered")}>
              <FormRow label="VAT registration number">
                <Input name="vatRegistrationNumber" />
              </FormRow>
            </Show>
            <FormRow
              label={
                <div class="flex items-center gap-2">
                  <span>Accounts Office reference number</span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowAccountReferenceNumberInfo(true);
                    }}
                  >
                    <HelpCircle class="ml-2 !h-5 !w-5 text-blue-700" />
                  </button>
                </div>
              }
            >
              <Input name="accountsOfficeReferenceNumber" />
            </FormRow>
            <div class="border-b py-4">
              <div class="flex flex-row items-center justify-between px-8">
                <FormLabel>PAYE reference number</FormLabel>
                <div class="flex flex-row items-center gap-2">
                  <Show
                    when={formMethods.errors("payeReferenceNumbers")?.at(0)}
                  >
                    <div class="text-red-500">
                      {formMethods.errors("payeReferenceNumbers")?.at(0)}
                    </div>
                  </Show>
                  <Button onClick={addPayeReferenceNumber}>
                    Add new PAYE reference number
                  </Button>
                </div>
              </div>
              <Index each={formMethods.data("payeReferenceNumbers") || []}>
                {(_, index) => {
                  return (
                    <SmsPayeReferenceInput
                      index={index}
                      name={`payeReferenceNumbers.${index}`}
                      removePayeReference={removePayeReference}
                    />
                  );
                }}
              </Index>
            </div>
            {/* <FormRow
            label="Is your organisation registered on stock exchange?"
            labelClass="!whitespace-normal"
          >
            <Checkbox name="registeredOnStockExchange" />
          </FormRow>

          <Show when={formMethods.data("registeredOnStockExchange")}>
            <FormRow label="Stock exchange name">
              <Input name="stockExchangeName" />
            </FormRow>
          </Show> */}
            <FormRow
              label={
                <div class="flex items-center gap-2">
                  <span>Care Quality Commission Registration Number</span>
                  <button
                    type="button"
                    onClick={() => {
                      setShowCareQualityGuidance(true);
                    }}
                  >
                    <HelpCircle class="ml-2 !h-5 !w-5 text-blue-700" />
                  </button>
                </div>
              }
              labelClass="!whitespace-normal"
            >
              <Input name="cqcNumber" />
            </FormRow>
            {/* <div class="border-b py-6">
            <div class="flex flex-row justify-between items-center px-8">
              <FormLabel>Registered to accredited bodies</FormLabel>
              <Button onClick={addRegisteredToAccreditedBodiesValue}>
                Add new accredited body
              </Button>
            </div>
            <Index
              each={formMethods.data("registeredToAccreditedBodies") || []}
            >
              {(_, index) => {
                return (
                  <FormRow
                    label={
                      <div class="flex items-center gap-4">
                        Accredited body {index + 1}
                        <button
                          onClick={() =>
                            removeRegisteredToAccreditedBodiesValue(index)
                          }
                          class="text-red-500"
                        >
                          <Trash class="w-5" />
                        </button>
                      </div>
                    }
                  >
                    <SmsAccreditedBodyInput
                      name={`registeredToAccreditedBodies.${index}`}
                    />
                  </FormRow>
                );
              }}
            </Index>
          </div> */}
            <FormRow
              label="Number of people in the UK that you would like to sponsor in the next 2 months"
              labelClass="!whitespace-normal"
            >
              <Input type="number" name="numberOfCos" />
            </FormRow>
            {/* <FormRow label="Reason for number of CoS">
            <TextArea
              name="reasonForNumberOfCos"
              class="p-2"
              value={COS_NUMBER_REASON}
            />
          </FormRow> */}

            {/* TODO: temporarily removed in case we find solution to different document types required */}

            {/* <FormRow label="Primary documents">
            <For each={smsOrganisationDocuments}>
              {(document) => {
                return (
                  <FormRow
                    label={document.name}
                    labelClass="!whitespace-normal"
                  >
                    <Checkbox name={`documents.${document.value}`} />
                  </FormRow>
                );
              }}
            </For>
          </FormRow> */}

            {/* <FormRow label="Other documents (you need at least 2)">
            <For each={smsOtherDocuments}>
              {(document) => {
                return (
                  <FormRow
                    label={document.name}
                    labelClass="!whitespace-normal"
                  >
                    <Checkbox name={`documents.${document.value}`} />
                  </FormRow>
                );
              }}
            </For>
          </FormRow> */}
            <FormRow
              label="Authorising Officer: Person who will have legal responsibility for sponsorship in your organisation"
              labelClass="!whitespace-normal self-start mt-6"
            >
              <SmsContactForm
                name="organisationAuthorisingOfficer"
                formData={formMethods.data("organisationAuthorisingOfficer")}
              />
              {/* <FormRow label="Is this key contact?">
              <Checkbox name="organisationAuthorisingOfficer.isKeyContact" />
            </FormRow> */}
            </FormRow>
            {/* <Show
            when={
              !formMethods.data("organisationAuthorisingOfficer.isKeyContact")
            }
          >
            <FormRow label="Key contact details">
              <SmsContactForm
                name="keyContact"
                formData={formMethods.data("keyContact")}
              />
            </FormRow>
          </Show> */}
            {/* <div class="border-b py-6">
            <div class="flex flex-row justify-between items-center px-8">
              <FormLabel>Level one users</FormLabel>
              <Button onClick={addLevelOneUser}>Add new level one users</Button>
            </div>
            <Index each={formMethods.data("levelOneUsers") || []}>
              {(_, index) => {
                return (
                  <FormRow
                    label={
                      <div class="flex items-center gap-4">
                        Level one user {index + 1}
                        <button
                          onClick={() => removeLevelOneUser(index)}
                          class="text-red-500"
                        >
                          <Trash class="w-5" />
                        </button>
                      </div>
                    }
                  >
                    <SmsContactForm
                      name={`levelOneUsers.${index}`}
                      formData={formMethods.data(`levelOneUsers`)[index]}
                    />
                  </FormRow>
                );
              }}
            </Index>
          </div> */}

            <FormRow
              label="Are you using agency staff to address staff shortages?"
              labelClass="!whitespace-normal"
            >
              <Checkbox name="usingAgencyStaff" />
            </FormRow>
            <FormRow
              label="Are you office staff carrying out care calls to cover staff shortages?"
              labelClass="!whitespace-normal"
            >
              <Checkbox name="officeStaffCarryingOutCareCalls" />
            </FormRow>
            <FormRow
              label="How many part time carers do you have?"
              labelClass="!whitespace-normal"
            >
              <Input type="number" name="numberOfPartTimeCarers" />
            </FormRow>
            <FormRow
              label="How many full time carers do you have?"
              labelClass="!whitespace-normal"
            >
              <Input type="number" name="numberOfFullTimeCarers" />
            </FormRow>
            <FormRow
              label="How many carers have resigned / left in the last 8 weeks?"
              labelClass="!whitespace-normal"
            >
              <Input type="number" name="numberOfCarersResigned" />
            </FormRow>
            <FormRow
              label="Have you tried to recruit from within the UK?"
              labelClass="!whitespace-normal"
            >
              <TextArea name="triedToRecruitFromWithinTheUK" />
            </FormRow>

            <FormRow
              label="Why are you applying for a sponsor licence?"
              labelClass="!whitespace-normal"
            >
              <TextArea name="whyApplyingForSponsorLicence" />
            </FormRow>
            <FormRow label="Agreement date">
              <DateInput name="agreeWithAllTheStatementsDate" />
            </FormRow>
            <FormRow
              label="Tick to confirm that you have read and understood the guidance for sponsor duties and compliance"
              subLabel={
                <>
                  <a
                    href="https://www.gov.uk/government/publications/workers-and-temporary-workers-guidance-for-sponsors-part-3-sponsor-duties-and-compliance/workers-and-temporary-workers-guidance-for-sponsors-part-3-sponsor-duties-and-compliance-accessible"
                    target="_blank"
                    class="flex items-center text-primary-700"
                  >
                    Sponsor Duties and Compliance{" "}
                    <LinkExternal01 class="ml-1 size-4" />
                  </a>
                </>
              }
              labelClass="!whitespace-normal"
            >
              <Checkbox name="hasReadGuidance" />
            </FormRow>

            <div class="flex w-full justify-end p-4">
              <Button type="submit">Save</Button>
            </div>
          </CardContainer>
          <Modal
            show={showFinalAssumptionsModal()}
            onClose={() => {
              setShowFinalAssumptionsModal(false);
            }}
          >
            <ModalHeader>We have made some assumptions</ModalHeader>
            <ModalBody class="space-y-4">
              <div>
                In order to simplify this form for you, we have made some
                assumptions:
              </div>
              <ul>
                <li>1. The authorising officer is the key contact person</li>
                <li>2. The authorising officer is the level one user</li>
                <li>
                  3. The authorising officer has not been convicted or bankrupt
                  in past
                </li>
                <li>
                  4. The authorising officer is British citizen or has settled
                  status
                </li>
              </ul>
              <div>
                If any of these are not correct, please contact our support for
                more help.
              </div>
            </ModalBody>
            <ModalFooter class="justify-end">
              <Button
                onClick={() => {
                  setShowFinalAssumptionsModal(false);
                }}
              >
                Close
              </Button>
            </ModalFooter>
          </Modal>
          <Modal
            show={showCareQualityGuidance()}
            onClose={() => {
              setShowCareQualityGuidance(false);
            }}
          >
            <ModalHeader>
              Care Quality Commission registration number
            </ModalHeader>
            <ModalBody>
              <p>
                Your CQC registration number is your Provider ID. It can be
                found on your CQC certificate of Registration which is usually
                on display in your office.
              </p>
              <p>
                You can also find it on your listing on the CQC website. Simply
                google your company name and CQC.
              </p>
              <p>
                Click on your listing. The numbers at the end of the URL on this
                page is your Provider ID.
              </p>
              <p>
                Here is an example:{" "}
                <strong>https://www.cqc.org.uk/provider/1-5658894207</strong>.
              </p>
              <p>
                The provider ID here is <strong>1-5658894207</strong>.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setShowCareQualityGuidance(false)}>
                Close
              </Button>
            </ModalFooter>
          </Modal>
          <Modal
            show={showAccountReferenceNumberInfo()}
            onClose={() => {
              setShowAccountReferenceNumberInfo(false);
            }}
          >
            <ModalHeader>Accounts Office Reference Number</ModalHeader>
            <ModalBody>
              <p>
                The Accounts Office Reference Number is the reference used by an
                employer when making payments to HMRC.
              </p>
              <p>
                This is 13 characters, like <strong>123PX00123456</strong> or{" "}
                <strong>123PX0012345X</strong>.{" "}
              </p>
              <p>
                It will be on letters from HMRC about PAYE and when you
                registered as an employer. You can also find it on emails from
                HMRC and PAYE bills.
              </p>
            </ModalBody>
            <ModalFooter>
              <Button onClick={() => setShowAccountReferenceNumberInfo(false)}>
                Close
              </Button>
            </ModalFooter>
          </Modal>
        </form>
      </FormContextProvider>
    </Show>
  );
};

export default SmsForm;

const SmsContactForm: Component<{ name: string; formData: SmsContact }> = (
  props,
) => {
  return (
    <div class="rounded-lg border">
      <Show when={props.formData}>
        <FormRow label="Title">
          <Select name={`${props.name}.title`} options={smsTitleOptions} />
        </FormRow>
        <Show when={props.formData.title === "ZZ  "}>
          <FormRow label="Other title">
            <Input name={`${props.name}.otherTitle`} />
          </FormRow>
        </Show>
        <FormRow label="First name">
          <Input name={`${props.name}.firstName`} />
        </FormRow>
        <FormRow label="Last name">
          <Input name={`${props.name}.lastName`} />
        </FormRow>
        <FormRow label="Previous name">
          <Input name={`${props.name}.previousName`} />
        </FormRow>
        <FormRow label="Employment address">
          <div class="rounded-lg border">
            <AddressInput
              name={`${props.name}.employmentAddress`}
              countryOptions={smsCountryOptions}
              editable
              containerClass="last:!border-b-0"
            />
          </div>
        </FormRow>
        <FormRow label="Email">
          <Input name={`${props.name}.employmentAddress.email`} />
        </FormRow>
        <FormRow label="Phone number">
          <Input name={`${props.name}.employmentAddress.phoneNumber`} />
        </FormRow>
        <FormRow label="Date of birth">
          <DateInput name={`${props.name}.employmentAddress.dateOfBirth`} />
        </FormRow>
        <FormRow label="Nationality country">
          <Select
            name={`${props.name}.employmentAddress.nationalityCountry`}
            options={countryOptions}
            showSearchBar
          />
        </FormRow>
        <FormRow
          label="Position within organisation"
          labelClass="!whitespace-normal"
        >
          <Input
            name={`${props.name}.employmentAddress.positionWithinOrganisation`}
          />
        </FormRow>
        {/* <FormRow
          label="Has national insurance number"
          labelClass="!whitespace-normal"
        >
          <Checkbox name={`${props.name}.hasNationalInsuranceNumber`} />
        </FormRow> */}
        {/* <Show
          when={props.formData.hasNationalInsuranceNumber}
          fallback={
            <FormRow
              label="National insurance number exemption"
              labelClass="!whitespace-normal"
            >
              <Input name={`${props.name}.nationalInsuranceNumberExemption`} />
            </FormRow>
          }
        > */}
        <FormRow
          label="National insurance number"
          labelClass="!whitespace-normal"
          containerClass="!border-b-0"
        >
          <Input name={`${props.name}.nationalInsuranceNumber`} />
        </FormRow>
        {/* </Show> */}
        {/* 
        <FormRow
          label="Is non-settled under immigration control?"
          labelClass="!whitespace-normal"
        >
          <Checkbox
            name={`${props.name}.isNonSettledUnderImmigrationControl`}
          />
        </FormRow>
        <Show when={props.formData.isNonSettledUnderImmigrationControl}>
          <FormRow label="Immigration status">
            <Input name={`${props.name}.immigrationStatus`} />
          </FormRow>
          <FormRow label="Home office reference">
            <Input name={`${props.name}.homeOfficeReference`} />
          </FormRow>
          <FormRow
            label="Date current leave to enter or leave ends"
            labelClass="!whitespace-normal"
          >
            <DateInput
              name={`${props.name}.dateCurrentLeaveToEnterOrLeaveEnds`}
            />
          </FormRow>
          <FormRow label="Passport number">
            <Input name={`${props.name}.passportNumber`} />
          </FormRow>
        </Show>
        <FormRow
          label="Has officer been convicted or bankrupt?"
          labelClass="!whitespace-normal"
        >
          <Checkbox name={`${props.name}.hasOfficerBeenConvictedOrBankrupt`} />
        </FormRow>
        <Show when={props.formData.hasOfficerBeenConvictedOrBankrupt}>
          <FormRow
            label="Conviction or bankrupt details"
            labelClass="!whitespace-normal"
          >
            <Input name={`${props.name}.convictionOrBankruptDetails`} />
          </FormRow>
        </Show> */}
      </Show>
    </div>
  );
};

// const SmsAccreditedBodyInput: Component<{ name: string }> = (props) => {
//   return (
//     <>
//       <FormRow label="Accredited body name">
//         <Input name={`${props.name}.bodyName`} />
//       </FormRow>
//       <FormRow label="Accreditation number">
//         <Input type="number" name={`${props.name}.bodyNumber`} />
//       </FormRow>
//       <FormRow label="Expiry date">
//         <DateInput name={`${props.name}.expiryDate`} />
//       </FormRow>
//     </>
//   );
// };

const SmsPayeReferenceInput: Component<{
  name: string;
  index: number;
  removePayeReference: (index: number) => void;
}> = (props) => {
  return (
    <FormRow
      label={
        <div class="flex items-center gap-4">
          PAYE reference #{props.index + 1}
          <button
            type="button"
            onClick={() => props.removePayeReference(props.index)}
            class="text-red-500"
          >
            <Trash01 class="w-5" />
          </button>
        </div>
      }
      containerClass="border-b-0"
    >
      <Input name={`${props.name}.payeReference`} />
    </FormRow>
  );
};

const SmsFormPreviousTradingName: Component<{ name: string }> = (props) => {
  return (
    <div class="rounded-lg border">
      <FormRow label="Previous trading name">
        <Input name={`${props.name}.tradingName`} />
      </FormRow>
      <FormRow label="Address">
        <div class="rounded-lg border">
          <AddressInput
            name={`${props.name}.address`}
            countryOptions={smsCountryOptions}
            editable
            containerClass="last:!border-b-0"
          />
        </div>
      </FormRow>
      <FormRow
        label="Previous trading phone number"
        labelClass="!whitespace-normal"
      >
        <Input name={`${props.name}.phoneNumber`} />
      </FormRow>
      <FormRow label="Website">
        <Input name={`${props.name}.website`} />
      </FormRow>
      <FormRow label="Trading from">
        <DateInput name={`${props.name}.tradingFrom`} />
      </FormRow>
      <FormRow label="Trading to" containerClass="!border-b-0">
        <DateInput name={`${props.name}.tradingTo`} />
      </FormRow>
    </div>
  );
};
